import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { clearErrors, getOrderDetails, retryPayment } from "../../actions/orderAction";
import { load } from '@cashfreepayments/cashfree-js';

import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  useTheme,
  Button,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Stack,
} from "@mui/material";

import MetaData from "../Layouts/MetaData";
import MinCategory from "../Layouts/MinCategory";
import Loader from "../Layouts/Loader";
import { formatDate } from "../../utils/functions";

const OrderDetails = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const { order, error, loading } = useSelector(
    (state) => state.orderDetails
  );

  const { payment, retryError, loading: retryLoading } = useSelector(
    (state) => state.retryPayment
  );

  const [activeStep, setActiveStep] = useState(0);

  /* ------------------ Styles ------------------ */

  const paperStyle = {
    p: 3,
    borderRadius: 3,
    bgcolor: "background.paper",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 4px 20px rgba(0,0,0,0.6)"
        : theme.shadows[3],
  };

  /* ------------------ Effects ------------------ */

  useEffect(() => {
    dispatch(getOrderDetails(params.id));
  }, [dispatch, params.id]);

  useEffect(() => {
    if (order && order?.paymentInfo?.status === 'success') {
      const statusMap = {
        Processing: 0,
        Shipped: 1,
        Delivered: 2,
      };

      setActiveStep(statusMap[order.orderStatus] ?? 0);
    } else {
      setActiveStep(0); // Default to processing if payment not success
    }
  }, [order]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
      dispatch(clearErrors());
    }
  }, [dispatch, error, enqueueSnackbar]);

  useEffect(() => {
    if (retryError) {
      enqueueSnackbar(retryError, { variant: "error" });
      dispatch(clearErrors());
    }
  }, [dispatch, retryError, enqueueSnackbar]);

  // Refetch order details when coming back from payment to ensure payment status is updated
  useEffect(() => {
    const handlePageFocus = () => {
      console.log('Page regained focus, refetching order details');
      dispatch(getOrderDetails(params.id));
    };

    window.addEventListener('focus', handlePageFocus);
    
    // Also refetch after a short delay in case callback is still processing
    const timer = setTimeout(() => {
      dispatch(getOrderDetails(params.id));
    }, 1000);

    return () => {
      window.removeEventListener('focus', handlePageFocus);
      clearTimeout(timer);
    };
  }, [dispatch, params.id]);

  useEffect(() => {
    if (payment && payment.payment_session_id) {
      console.log('Payment received, initiating checkout:', payment);
      // Load Cashfree and initiate checkout
      const initiateCheckout = async () => {
        try {
          const cashfree = await load({
            mode: process.env.REACT_APP_CASHFREE_MODE || 'sandbox',
            appId: process.env.REACT_APP_CASHFREE_APP_ID
          });

          const checkoutOptions = {
            paymentSessionId: payment.payment_session_id,
            redirectTarget: "_self", // Use full page redirect like initial payment
          };

          console.log('Initiating Cashfree checkout with options:', checkoutOptions);
          await cashfree.checkout(checkoutOptions);
        } catch (err) {
          console.error('Cashfree checkout error:', err);
          enqueueSnackbar('Failed to load payment gateway. Please try again.', { variant: 'error' });
        }
      };
      initiateCheckout();
    }
  }, [payment, enqueueSnackbar]);

  /* ------------------ Loading ------------------ */

  if (loading) return <Loader />;

  if (!order || !order.user || !order.shippingInfo) {
    return (
      <Box textAlign="center" mt={5}>
        <Typography variant="h6">Order not found</Typography>
        <Button component={Link} to="/orders" variant="contained" sx={{ mt: 2 }}>
          Back to Orders
        </Button>
      </Box>
    );
  }

  /* ------------------ Data ------------------ */

  const { user, shippingInfo, orderItems, _id, orderStatus } = order;
  const paymentStatus = order?.paymentInfo?.status;
  const paymentMessage = order?.paymentInfo?.message;
  const paidAt = order?.paidAt;

  const itemsTotal = orderItems.reduce(
    (acc, item) => acc + item.quantity * item.price,
    0
  );

  // Determine display status based on payment
  const getDisplayStatus = () => {
    if (!paymentStatus) return orderStatus;
    switch (paymentStatus) {
      case 'success':
        return 'Payment Successful';
      case 'pending':
        return 'Payment Pending';
      case 'failed':
      case 'user_dropped':
        return 'Payment Failed';
      default:
        return orderStatus;
    }
  };

  const getStatusColor = () => {
    if (!paymentStatus) return orderStatus === "Delivered" ? "success" : orderStatus === "Shipped" ? "info" : "warning";
    switch (paymentStatus) {
      case 'success':
        return orderStatus === "Delivered" ? "success" : orderStatus === "Shipped" ? "info" : "warning";
      case 'pending':
        return 'warning';
      case 'failed':
      case 'user_dropped':
        return 'error';
      default:
        return 'default';
    }
  };

  /* ------------------ UI ------------------ */

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        py: 5,
      }}
    >
      <MetaData title={`Order Details - #${_id}`} />
      <MinCategory />

      <Box sx={{ width: { xs: "95%", md: "80%", lg: "70%" }, mx: "auto" }}>
        {/* HEADER */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h4" fontWeight={700}>
            Order #{_id}
          </Typography>

          <Chip
            label={getDisplayStatus()}
            color={getStatusColor()}
          />
        </Stack>

        {/* STEPPER */}
        {paymentStatus === 'success' && (
          <Paper sx={{ ...paperStyle, mb: 3 }}>
            <Typography variant="h6" mb={2}>
              Order Timeline
            </Typography>

            <Stepper activeStep={activeStep} alternativeLabel>
              {["Processing", "Shipped", "Delivered"].map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>
        )}

        {/* GRID */}
        <Grid container spacing={3}>
          {/* LEFT COLUMN */}
          <Grid item xs={12} md={8}>
            {/* ---------------- TRACKING ---------------- */}
            {order?.tracking?.length > 0 && (
              <Paper sx={{ ...paperStyle, mb: 3 }}>
                <Typography variant="h6" mb={3}>
                  Tracking Updates
                </Typography>

                <Box sx={{ position: "relative", pl: 3 }}>
                  {/* vertical line */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 10,
                      top: 0,
                      bottom: 0,
                      width: 2,
                      bgcolor: "divider",
                    }}
                  />

                  <Stack spacing={3}>
                    {[...order.tracking]
                      .sort(
                        (a, b) =>
                          new Date(b.timestamp) - new Date(a.timestamp)
                      )
                      .map((track, index) => {
                        const isLatest = index === 0;

                        return (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              gap: 2,
                              alignItems: "flex-start",
                            }}
                          >
                            {/* dot */}
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: "50%",
                                bgcolor: isLatest
                                  ? "primary.main"
                                  : "divider",
                                border: "3px solid",
                                borderColor: "background.paper",
                              }}
                            />

                            {/* content */}
                            <Box
                              sx={{
                                flex: 1,
                                p: 2,
                                borderRadius: 2,
                                bgcolor: isLatest
                                  ? "primary.main"
                                  : "background.paper",
                                color: isLatest
                                  ? "primary.contrastText"
                                  : "text.primary",
                                boxShadow: 1,
                              }}
                            >
                              <Typography fontWeight={600}>
                                {track.location}
                              </Typography>

                              {track.message && (
                                <Typography variant="body2">
                                  {track.message}
                                </Typography>
                              )}

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatDate(track.timestamp)}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                  </Stack>
                </Box>
              </Paper>
            )}

            {/* ---------------- ITEMS ---------------- */}
            {orderItems.map((item) => (
              <Paper
                key={item._id}
                sx={{
                  ...paperStyle,
                  mb: 2,
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  transition: "0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: 6,
                  },
                }}
              >
                <img
                  src={item.image}
                  alt={item.name}
                  style={{
                    width: 90,
                    height: 90,
                    objectFit: "cover",
                    borderRadius: 12,
                  }}
                />

                <Box flex={1}>
                  <Typography fontWeight={600}>{item.name}</Typography>

                  <Typography variant="body2" color="text.secondary">
                    Qty: {item.quantity} × ₹{item.price}
                  </Typography>
                </Box>

                <Typography fontWeight={700}>
                  ₹{(item.quantity * item.price).toLocaleString()}
                </Typography>
              </Paper>
            ))}
          </Grid>

          {/* RIGHT COLUMN (SUMMARY) */}
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                ...paperStyle,
                position: "sticky",
                top: 20,
              }}
            >
              <Typography variant="h6" mb={2}>
                Order Summary
              </Typography>

              <Stack spacing={1}>
                <Typography>Name: {user.name}</Typography>
                <Typography>Email: {user.email}</Typography>
                <Typography>Phone: {shippingInfo.phoneNo}</Typography>
                <Typography>
                  Address: {shippingInfo.address}, {shippingInfo.city}
                </Typography>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={1}>
                <Typography>Payment Status: 
                  <Chip 
                    label={paymentStatus || 'Unknown'} 
                    color={
                      paymentStatus === 'success' ? 'success' :
                      paymentStatus === 'pending' ? 'warning' :
                      paymentStatus === 'failed' || paymentStatus === 'user_dropped' ? 'error' :
                      'default'
                    }
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                {paymentMessage && (
                  <Typography variant="body2" color="text.secondary">
                    {paymentMessage}
                  </Typography>
                )}
                {paymentStatus && paymentStatus !== 'success' && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      console.log('Retry payment clicked for order:', _id);
                      dispatch(retryPayment(_id));
                    }}
                    disabled={retryLoading}
                    sx={{ mt: 1 }}
                    fullWidth
                  >
                    {retryLoading ? 'Processing...' : 'Retry Payment'}
                  </Button>
                )}
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography>Items Total</Typography>
                  <Typography fontWeight={600}>
                    ₹{itemsTotal.toLocaleString()}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography>Shipping</Typography>
                  <Typography>Free</Typography>
                </Stack>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" justifyContent="space-between">
                <Typography fontWeight={700}>Total</Typography>
                <Typography fontWeight={800}>
                  ₹{itemsTotal.toLocaleString()}
                </Typography>
              </Stack>

              <Button
                component={Link}
                to="/orders"
                variant="contained"
                fullWidth
                sx={{ mt: 3, borderRadius: 2 }}
              >
                Back to Orders
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default OrderDetails;
